import React from 'react';
import { Slider } from '../../../components/Slider';
import { CollapsibleSection } from '../../../components/CollapsibleSection';
import { RAW, FONT_SIZE, SPACE } from '../../../theme';
import {
  ComposeTabProps, SectionLabel, SelectBox, ActionButton, InfoText, TagBadge,
  abilityRegistry,
} from './shared';

export const ComposeTab: React.FC<ComposeTabProps> = ({ ability, update, composeSourceId, setComposeSourceId }) => {
  const allAbilities = abilityRegistry.getAll();
  const sourceAbility = composeSourceId ? abilityRegistry.getMetadata(composeSourceId) : null;

  const copyProjectile = () => {
    if (!sourceAbility?.projectile) return;
    update({ projectile: structuredClone(sourceAbility.projectile) });
  };

  const copyVfx = () => {
    if (!sourceAbility?.vfx) return;
    update({ vfx: structuredClone(sourceAbility.vfx) });
  };

  const copyBehaviors = () => {
    if (!sourceAbility?.behaviors) return;
    update({ behaviors: structuredClone(sourceAbility.behaviors) });
  };

  return (
    <>
      <SectionLabel>Source Ability</SectionLabel>
      <SelectBox value={composeSourceId} onChange={e => setComposeSourceId(e.target.value)}>
        <option value="">-- Select ability --</option>
        {allAbilities.map(a => (
          <option key={a.id} value={a.id}>{a.name || a.id}</option>
        ))}
      </SelectBox>

      {sourceAbility && (
        <>
          <SectionLabel>Copy From: {sourceAbility.name || sourceAbility.id}</SectionLabel>

          {sourceAbility.projectile && (
            <ActionButton $variant="blue" onClick={copyProjectile}>
              Copy Projectile ({sourceAbility.projectile.shape || 'rect'}, spd {sourceAbility.projectile.speed})
            </ActionButton>
          )}

          {sourceAbility.vfx && (
            <ActionButton $variant="blue" onClick={copyVfx}>
              Copy VFX ({sourceAbility.vfx.muzzleFlash || 'none'})
            </ActionButton>
          )}

          {sourceAbility.behaviors && sourceAbility.behaviors.length > 0 && (
            <ActionButton $variant="blue" onClick={copyBehaviors}>
              Copy Behaviors ({sourceAbility.behaviors.map(b => b.type).join(', ')})
            </ActionButton>
          )}

          {!sourceAbility.projectile && !sourceAbility.vfx && (!sourceAbility.behaviors || sourceAbility.behaviors.length === 0) && (
            <InfoText>No copyable configs on this ability.</InfoText>
          )}

          <SectionLabel>Source Details</SectionLabel>
          <div style={{ fontSize: FONT_SIZE.xs, color: RAW.gray }}>
            {sourceAbility.tags?.map(t => <TagBadge key={t}>{t}</TagBadge>)}
          </div>
          {sourceAbility.manaCost != null && (
            <div style={{ fontSize: FONT_SIZE.xs, color: RAW.gray, marginTop: SPACE.xs }}>
              Mana: {sourceAbility.manaCost} | CD: {sourceAbility.cooldown ?? '—'}ms
            </div>
          )}
        </>
      )}

      {!composeSourceId && (
        <InfoText>Select an ability above to copy its projectile, VFX, or behavior configs into this ability.</InfoText>
      )}

      <SectionLabel>Element</SectionLabel>
      <SelectBox
        value={ability.element || ''}
        onChange={e => update({ element: e.target.value || undefined })}
      >
        <option value="">-- No element --</option>
        {['fire', 'frost', 'shadow', 'holy', 'arcane', 'nature', 'lightning', 'physical'].map(el => (
          <option key={el} value={el}>{el.charAt(0).toUpperCase() + el.slice(1)}</option>
        ))}
      </SelectBox>

      <SectionLabel>Projectile Modifiers</SectionLabel>
      <CollapsibleSection title="Pierce" defaultOpen={false}>
        <Slider label="Pierce Count" value={ability.projectile?.modifiers?.pierce?.count ?? 0} min={0} max={10} step={1}
          onChange={v => update({ projectile: { ...ability.projectile, modifiers: { ...ability.projectile?.modifiers, pierce: v > 0 ? { count: v } : undefined } } })} />
      </CollapsibleSection>
      <CollapsibleSection title="Bounce" defaultOpen={false}>
        <Slider label="Bounce Count" value={ability.projectile?.modifiers?.bounce?.count ?? 0} min={0} max={8} step={1}
          onChange={v => update({ projectile: { ...ability.projectile, modifiers: { ...ability.projectile?.modifiers, bounce: v > 0 ? { count: v, range: ability.projectile?.modifiers?.bounce?.range ?? 200 } : undefined } } })} />
        {(ability.projectile?.modifiers?.bounce?.count ?? 0) > 0 && (
          <Slider label="Bounce Range" value={ability.projectile?.modifiers?.bounce?.range ?? 200} min={50} max={500} step={25}
            onChange={v => update({ projectile: { ...ability.projectile, modifiers: { ...ability.projectile?.modifiers, bounce: { ...ability.projectile?.modifiers?.bounce!, range: v } } } })} />
        )}
      </CollapsibleSection>
      <CollapsibleSection title="Chain" defaultOpen={false}>
        <Slider label="Chain Count" value={ability.projectile?.modifiers?.chain?.count ?? 0} min={0} max={8} step={1}
          onChange={v => update({ projectile: { ...ability.projectile, modifiers: { ...ability.projectile?.modifiers, chain: v > 0 ? { count: v, range: ability.projectile?.modifiers?.chain?.range ?? 200, damageReduction: 0.2 } : undefined } } })} />
      </CollapsibleSection>
      <CollapsibleSection title="Split" defaultOpen={false}>
        <Slider label="Split Count" value={ability.projectile?.modifiers?.split?.count ?? 0} min={0} max={8} step={1}
          onChange={v => update({ projectile: { ...ability.projectile, modifiers: { ...ability.projectile?.modifiers, split: v > 0 ? { count: v, spreadAngle: 45, damagePercent: 0.5 } : undefined } } })} />
      </CollapsibleSection>
      <CollapsibleSection title="AoE on Impact" defaultOpen={false}>
        <Slider label="AoE Radius" value={ability.projectile?.modifiers?.aoeOnImpact?.radius ?? 0} min={0} max={200} step={10}
          onChange={v => update({ projectile: { ...ability.projectile, modifiers: { ...ability.projectile?.modifiers, aoeOnImpact: v > 0 ? { radius: v, damagePercent: ability.projectile?.modifiers?.aoeOnImpact?.damagePercent ?? 0.5 } : undefined } } })} />
      </CollapsibleSection>

      <SectionLabel>On-Hit Effects</SectionLabel>
      <div style={{ fontSize: FONT_SIZE.xs, color: RAW.gray, marginBottom: SPACE.xs }}>
        Active: {ability.onHitEffects?.length ?? 0} effect(s)
        {ability.onHitEffects?.map((e, i) => (
          <TagBadge key={i}>{e.trigger}: {e.id || 'custom'}</TagBadge>
        ))}
      </div>
      <InfoText style={{ marginTop: 0 }}>On-hit effects are set via code (.withOnHit, .withOnCrit, .withOnKill)</InfoText>

      <SectionLabel>Damage Type</SectionLabel>
      <SelectBox
        value={ability.damageType || ''}
        onChange={e => update({ damageType: e.target.value || undefined })}
      >
        <option value="">-- Auto (from element) --</option>
        <option value="physical">Physical</option>
        <option value="magical">Magical</option>
        <option value="true">True</option>
      </SelectBox>

      <SectionLabel>Charges</SectionLabel>
      <Slider label="Max Charges" value={ability.charges?.maxCharges ?? 0} min={0} max={5} step={1}
        onChange={v => update({ charges: v > 0 ? { maxCharges: v, regenTimeMs: ability.charges?.regenTimeMs ?? 5000, consumePerCast: 1 } : undefined })} />
      {(ability.charges?.maxCharges ?? 0) > 0 && (
        <Slider label="Regen Time (ms)" value={ability.charges?.regenTimeMs ?? 5000} min={1000} max={30000} step={1000}
          onChange={v => update({ charges: { ...ability.charges, regenTimeMs: v } })} />
      )}

      <SectionLabel>Resource</SectionLabel>
      <SelectBox
        value={ability.resource?.type || ''}
        onChange={e => update({ resource: e.target.value ? { type: e.target.value, cost: ability.resource?.cost, generate: ability.resource?.generate } : undefined })}
      >
        <option value="">-- Mana (default) --</option>
        <option value="rage">Rage</option>
        <option value="focus">Focus</option>
        <option value="combo_points">Combo Points</option>
        <option value="holy_power">Holy Power</option>
      </SelectBox>
      {ability.resource?.type && (
        <>
          <Slider label="Cost" value={ability.resource?.cost ?? 0} min={0} max={100} step={5}
            onChange={v => update({ resource: { ...ability.resource, cost: v } })} />
          <Slider label="Generate" value={ability.resource?.generate ?? 0} min={0} max={20} step={1}
            onChange={v => update({ resource: { ...ability.resource, generate: v } })} />
        </>
      )}

      <SectionLabel>Flags</SectionLabel>
      <div style={{ display: 'flex', gap: SPACE.md, marginBottom: SPACE.md }}>
        <label style={{ fontSize: FONT_SIZE.xs, color: RAW.silver, display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
          <input type="checkbox" checked={!!ability.isToggle} onChange={e => update({ isToggle: e.target.checked || undefined })} />
          Toggle
        </label>
        <label style={{ fontSize: FONT_SIZE.xs, color: RAW.silver, display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
          <input type="checkbox" checked={!!ability.isPassive} onChange={e => update({ isPassive: e.target.checked || undefined })} />
          Passive
        </label>
      </div>

      <SectionLabel>Combo Chain</SectionLabel>
      <SelectBox
        value={ability.chainInto?.abilityId || ''}
        onChange={e => update({ chainInto: e.target.value ? { abilityId: e.target.value, windowMs: ability.chainInto?.windowMs ?? 2000 } : undefined })}
      >
        <option value="">-- No chain --</option>
        {abilityRegistry.getAll().filter(a => a.id !== ability.id).map(a => (
          <option key={a.id} value={a.id}>{a.name || a.id}</option>
        ))}
      </SelectBox>
      {ability.chainInto?.abilityId && (
        <Slider label="Window (ms)" value={ability.chainInto?.windowMs ?? 2000} min={500} max={5000} step={250}
          onChange={v => update({ chainInto: { ...ability.chainInto, windowMs: v } })} />
      )}

      <SectionLabel>Ground Target</SectionLabel>
      <Slider label="Max Range" value={ability.groundTarget?.maxRange ?? 0} min={0} max={500} step={25}
        onChange={v => update({ groundTarget: v > 0 ? { maxRange: v, indicatorRadius: ability.groundTarget?.indicatorRadius ?? 50 } : undefined })} />
    </>
  );
};
